/**
 * The Cisco-inventory preset: the first domain implementation, ported from the standalone
 * `Workshop-assessment-temp` app. It recognises a "Cisco Ready"-style product/architecture export (a
 * customer's bill of Cisco gear), fuzzy-matches each row against the catalog, and derives purpose-built
 * widgets — an inventory-by-category bar, an estate-coverage donut, a matched-products table, and the
 * auto-drawn isometric architecture diagram.
 *
 * The catalog + matcher (`catalog/`) and topology derivation (`../topology/`) are the ported pure logic;
 * this file only orchestrates them into `WidgetSpec`s and confidence scoring.
 */
import { ParsedDataset } from '../parseDataset';
import { ImportPreset, PresetBuildResult, WidgetSpec } from './types';
import { matchLabel } from '../catalog/match';
import { getEntityItem, getEntityCategory } from '../catalog/entityCatalog';
import { resolveRows, MatchedEntity } from '../catalog/resolve';
import { buildTopology } from '../topology/buildTopology';

const CONFIDENCE_THRESHOLD = 0.25;

/** How well one column's values match the catalog, in [0, 1]. Used to pick the item-label column. */
function columnMatchRate(dataset: ParsedDataset, column: string): number {
  const rows = dataset.rows;
  if (rows.length === 0) return 0;
  let hits = 0;
  let considered = 0;
  for (const row of rows) {
    const raw = row[column];
    if (raw === null || raw === undefined || String(raw).trim() === '') continue;
    considered += 1;
    const candidates = matchLabel(String(raw));
    if (candidates.some((c) => c.confidence === 'exact' || c.confidence === 'alias')) hits += 1;
  }
  return considered ? hits / considered : 0;
}

/** The column whose values best match the catalog, and its match rate. */
function bestItemColumn(dataset: ParsedDataset): { column: string | null; rate: number } {
  let best: { column: string | null; rate: number } = { column: null, rate: 0 };
  for (const column of dataset.columns) {
    const rate = columnMatchRate(dataset, column);
    if (rate > best.rate) best = { column, rate };
  }
  return best;
}

/** A category-ish column to carry through as `rawCategory` (a column named like a family/group). */
function guessCategoryColumn(dataset: ParsedDataset, itemColumn: string | null): string | null {
  const aliases = ['category', 'group', 'family', 'architecture', 'row labels'];
  for (const column of dataset.columns) {
    if (column === itemColumn) continue;
    if (aliases.includes(column.trim().toLowerCase())) return column;
  }
  return null;
}

function detect(dataset: ParsedDataset): number {
  const { rate } = bestItemColumn(dataset);
  return rate >= CONFIDENCE_THRESHOLD ? rate : 0;
}

function build(dataset: ParsedDataset): PresetBuildResult {
  const { column: itemColumn } = bestItemColumn(dataset);
  const categoryColumn = guessCategoryColumn(dataset, itemColumn);

  const rawRows = dataset.rows
    .map((row) => ({
      label: itemColumn ? String(row[itemColumn] ?? '').trim() : '',
      rawCategory: categoryColumn ? String(row[categoryColumn] ?? '').trim() : undefined,
    }))
    .filter((r) => r.label);

  const { matched, unmatched } = resolveRows(rawRows);

  // --- Widget A: inventory by category (matched items grouped by catalog category) ---
  const byCategory = new Map<string, number>();
  for (const m of matched) {
    const item = getEntityItem(m.itemId);
    const category = item ? getEntityCategory(item.categoryId) : undefined;
    const label = category?.label || 'Other';
    byCategory.set(label, (byCategory.get(label) || 0) + 1);
  }
  const inventoryWidget: WidgetSpec = {
    id: 'cisco-inventory-by-category',
    title: 'Inventory by category',
    chartType: 'vertical_bar_chart',
    data: Array.from(byCategory.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value),
    settings: { labelField: 'label', valueField: 'value' },
    note: `${matched.length} products matched across ${byCategory.size} categories`,
  };

  // --- Widget B: estate coverage (matched vs. needs review) ---
  const coverageWidget: WidgetSpec = {
    id: 'cisco-estate-coverage',
    title: 'Estate coverage',
    chartType: 'donut_chart',
    data: [
      { label: 'Matched', value: matched.length },
      { label: 'Needs review', value: unmatched.length },
    ],
    settings: { labelField: 'label', valueField: 'value' },
    note: `${unmatched.length} row(s) need review`,
  };

  // --- Widget C: matched products table ---
  const tableWidget: WidgetSpec = {
    id: 'cisco-matched-products',
    title: 'Matched products',
    chartType: 'datagrid',
    data: matched.map((m: MatchedEntity) => {
      const item = getEntityItem(m.itemId);
      const category = item ? getEntityCategory(item.categoryId) : undefined;
      return {
        Product: item?.label || m.itemId,
        Category: category?.label || '—',
        Source: m.rawLabel,
        Match: m.confidence,
      };
    }),
    settings: { selectedColumns: ['Product', 'Category', 'Source', 'Match'] },
    note: `${matched.length} products`,
  };

  // --- Widget D: derived isometric architecture (topology) ---
  const topoModel = buildTopology({
    entities: matched.map((m) => ({ itemId: m.itemId })),
    useCases: [],
  });
  const topologyWidget: WidgetSpec = {
    id: 'cisco-architecture',
    title: 'Derived architecture',
    chartType: 'topology',
    data: [],
    payload: topoModel,
    note: `${topoModel.nodes.length} nodes, ${topoModel.edges.length} links (auto-drawn)`,
  };

  return {
    widgets: [inventoryWidget, coverageWidget, tableWidget, topologyWidget],
    matched: matched.map((m) => {
      const item = getEntityItem(m.itemId);
      return { label: item?.label || m.itemId, detail: `${m.confidence} · from "${m.rawLabel}"` };
    }),
    unmatched: unmatched.map((u) => ({
      label: u.rawLabel,
      suggestions: (u.suggestions || []).map((s) => getEntityItem(s.itemId)?.label || s.itemId),
    })),
  };
}

export const ciscoInventoryPreset: ImportPreset = {
  key: 'cisco_inventory',
  label: 'Cisco inventory',
  description: 'Cisco Ready / product estate export → architecture widgets',
  detect,
  build,
};
